import { v5 as uuidv5 } from 'uuid';
import dayjs from '~/timeInit';

import { VOUCHER_TO_CREDIT_TRANSACTION_KEY_NAMESPACE } from '~/consts/credits';

type VoucherUserState = {
  usedAt?: string | null;
  expiredAt?: string | null;
};

type VoucherState = {
  deactivatedAt?: string | null;
  voucherUserExpiresAt?: string | null;
};

type BlockReasonParams = {
  /** Undefined when the assignment could not be found, e.g. it was deleted. */
  voucherUser: VoucherUserState | undefined;
  /** Undefined when the voucher could not be found, e.g. it was deleted. */
  voucher: VoucherState | undefined;
  isAlreadyCredited?: boolean;
  now?: number;
};

/**
 * Idempotency key for the credit granted when converting a voucher assignment.
 * It is derived from the assignment ID, so every attempt to convert the same assignment
 * (retries, page reloads, other tabs or agents) sends the same key and payments grants the credit only once.
 */
export function getVoucherToCreditTransactionKey(voucherUserId: string): string {
  return uuidv5(voucherUserId, VOUCHER_TO_CREDIT_TRANSACTION_KEY_NAMESPACE);
}

/** Returns why a voucher assignment cannot be converted to credit, or null when it can. */
export function getVoucherToCreditBlockReason({
  voucherUser,
  voucher,
  isAlreadyCredited = false,
  now = Date.now(),
}: BlockReasonParams): string | null {
  if (isAlreadyCredited) {
    return 'Credit already added for this voucher';
  }
  if (!voucher) {
    return 'Voucher details not found';
  }
  // The voucher itself must still be live, not just this assignment.
  if (
    (voucher.deactivatedAt && dayjs(voucher.deactivatedAt).isBefore(now)) ||
    (voucher.voucherUserExpiresAt && dayjs(voucher.voucherUserExpiresAt).isBefore(now))
  ) {
    return 'Voucher is no longer active';
  }
  if (!voucherUser) {
    return 'Voucher assignment not found';
  }
  if (voucherUser.usedAt) {
    return 'Voucher already used';
  }
  if (voucherUser.expiredAt && dayjs(voucherUser.expiredAt).isBefore(now)) {
    return 'Voucher already expired';
  }
  return null;
}
